/**
 * Arrows: vanilla projectile physics (gravity 0.05, drag 0.99 / 0.6 in water),
 * they stick in blocks for 1200 ticks, and hit hitbox AABBs (inflated 0.3 like
 * vanilla) along each tick's travel segment. Damage on hit = ceil(speed * base).
 */

import { collisionOf, IS_WATERY } from '../blocks';
import type { ArrowView } from '../game/types';
import type { World } from '../world/chunk';
import { segmentAabb, type EntityHost, type PlayerTarget } from './host';
import type { Mob } from './mob';
import { I } from '../game/itemIds';
import { wardenHooks } from './boss/wardenHooks';

const GRAVITY = 0.05;
const STUCK_LIFETIME = 1200;
const FLIGHT_LIFETIME = 1200;

export interface ArrowCtx {
  readonly host: EntityHost;
  readonly world: World;
  readonly mobs: readonly Mob[];
  /** Everyone this arrow may hit: the local player plus, in multiplayer, remotes. */
  readonly players: readonly PlayerTarget[];
  attackMob(mob: Mob, damage: number, fromX: number, fromZ: number, knockback: number, opts?: { fireTicks?: number; shooter?: number }): boolean;
  light(x: number, y: number, z: number): number;
}

export class Arrow {
  readonly id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  readonly fromPlayer: boolean;
  readonly damage: number;
  readonly shooterId: number;
  stuck = false;
  stuckTicks = 0;
  /** Fully drawn bow shot: + random(0 .. dmg/2 + 1) on hit. */
  crit = false;
  /** Crossbow arrows: a flat damage regardless of speed (Bedrock 9). */
  fixedDamage = 0;
  /** Punch level (+0.6 b/t horizontal knockback per level, vanilla). */
  punch = 0;
  /** Flame: sets the target alight for 5 s. */
  flame = false;
  /** Deflected off a shield: harmless from now on. */
  spent = false;
  /** A stuck player-fired arrow can be walked over to recover it (false for Infinity / creative shots). */
  pickup = false;
  /** Piercing: mobs it may pass through after the first. */
  pierce = 0;
  private readonly pierced = new Set<number>();
  life = 0;
  removed = false;
  private stuckCell = [0, 0, 0];
  readonly view: ArrowView;

  constructor(id: number, x: number, y: number, z: number, vx: number, vy: number, vz: number,
    damage: number, fromPlayer: boolean, shooterId: number) {
    this.id = id;
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    this.vx = vx; this.vy = vy; this.vz = vz;
    this.damage = damage;
    this.fromPlayer = fromPlayer;
    this.shooterId = shooterId;
    this.view = { id, x, y, z, px: x, py: y, pz: z, yaw: 0, pitch: 0, stuck: false, light: 1 };
    this.orient();
  }

  private orient(): void {
    const h = Math.hypot(this.vx, this.vz);
    if (h > 1e-6 || Math.abs(this.vy) > 1e-6) {
      this.view.yaw = Math.atan2(-this.vx, -this.vz);
      this.view.pitch = Math.atan2(this.vy, h);
    }
  }

  /** Is (x,y,z) inside a block's collision shape? */
  private static inBlock(w: World, x: number, y: number, z: number): boolean {
    const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
    const sh = collisionOf(w.getBlock(bx, by, bz));
    const lx = (x - bx) * 16, ly = (y - by) * 16, lz = (z - bz) * 16;
    for (let s = 0; s < sh.length; s += 6) {
      if (lx >= sh[s] && lx <= sh[s + 3] && ly >= sh[s + 1] && ly <= sh[s + 4] && lz >= sh[s + 2] && lz <= sh[s + 5]) return true;
    }
    return false;
  }

  tick(ctx: ArrowCtx): void {
    this.px = this.x; this.py = this.y; this.pz = this.z;
    const w = ctx.world;
    if (!w.getChunk(Math.floor(this.x) >> 4, Math.floor(this.z) >> 4) || this.y < -20) { this.removed = true; return; }

    if (this.stuck) {
      const c = this.stuckCell;
      if (collisionOf(w.getBlock(c[0], c[1], c[2])).length === 0) {
        // the block it was in is gone: fall again
        this.stuck = false;
        this.vx = this.vy = this.vz = 0;
        this.stuckTicks = 0;
      } else {
        if (++this.stuckTicks >= STUCK_LIFETIME) this.removed = true;
        // pickable: drop the arrow item on the player's feet so item pickup collects it
        // (the dedicated server has no host.player: any player standing on it takes it)
        const near = (q: { alive: boolean; x: number; y: number; z: number }): boolean => q.alive && Math.abs(q.x - this.x) < 1
          && Math.abs(q.z - this.z) < 1 && this.y > q.y - 0.5 && this.y < q.y + 2;
        const pl = near(ctx.host.player) ? ctx.host.player : ctx.players.find(near);
        if (this.pickup && this.stuckTicks > 5 && pl) {
          ctx.host.dropItem({ id: I.ARROW, count: 1, dmg: 0 }, pl.x, pl.y + 0.5, pl.z, 0, 0, 0);
          this.removed = true;
          return;
        }
        this.syncView(ctx);
        return;
      }
    }
    if (++this.life > FLIGHT_LIFETIME) { this.removed = true; return; }

    const dx = this.vx, dy = this.vy, dz = this.vz;
    const len = Math.hypot(dx, dy, dz);

    // nearest block hit along the segment (sub-steps of <= 0.2 blocks)
    let blockT = 2;
    const steps = Math.max(1, Math.ceil(len / 0.2));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      if (Arrow.inBlock(w, this.x + dx * t, this.y + dy * t, this.z + dz * t)) { blockT = t; break; }
    }

    // nearest entity hit
    let hitMob: Mob | null = null;
    let hitPlayer: PlayerTarget | null = null;
    let entT = 2;
    const reach = len + 0.3;
    // skeletons' arrows pass through other mobs (no friendly fire); the player's hit any
    if (this.fromPlayer) {
      for (const m of ctx.mobs) {
        if (!m.alive || m.id === this.shooterId || this.pierced.has(m.id)) continue;
        if (Math.abs(m.x - this.x) > reach + 2 || Math.abs(m.z - this.z) > reach + 2) continue;
        const hw = m.width / 2 + 0.3;
        const t = segmentAabb(this.x, this.y, this.z, dx, dy, dz,
          [m.x - hw, m.y - 0.3, m.z - hw, m.x + hw, m.y + m.height + 0.3, m.z + hw]);
        if (t >= 0 && t < entT) { entT = t; hitMob = m; }
      }
      // dedicated server (targets carry an entity id): a player's arrow hits other players too
      for (const p of ctx.players) {
        if (p.eid === undefined || p.eid === this.shooterId || this.spent || !p.alive || p.creative || p.spectator) continue;
        const hw = p.width / 2 + 0.3;
        const t = segmentAabb(this.x, this.y, this.z, dx, dy, dz,
          [p.x - hw, p.y - 0.3, p.z - hw, p.x + hw, p.y + p.height + 0.3, p.z + hw]);
        if (t >= 0 && t < entT) { entT = t; hitPlayer = p; hitMob = null; }
      }
    } else {
      for (const p of ctx.players) {
        if (this.spent || !p.alive || p.creative || p.spectator) continue;
        const hw = p.width / 2 + 0.3;
        const t = segmentAabb(this.x, this.y, this.z, dx, dy, dz,
          [p.x - hw, p.y - 0.3, p.z - hw, p.x + hw, p.y + p.height + 0.3, p.z + hw]);
        if (t >= 0 && t < entT) { entT = t; hitPlayer = p; hitMob = null; }
      }
    }

    if (entT <= blockT && entT <= 1 && (hitMob || hitPlayer)) {
      let dmg = this.fixedDamage > 0 ? this.fixedDamage : Math.ceil(len * this.damage);
      if (this.crit && this.fixedDamage <= 0) dmg += Math.floor(ctx.host.rnd() * (Math.floor(dmg / 2) + 2));
      this.x += dx * entT; this.y += dy * entT; this.z += dz * entT;
      if (hitMob) ctx.attackMob(hitMob, dmg, this.x - dx, this.z - dz, 0.4 + this.punch * 0.6, { fireTicks: this.flame ? 100 : 0, shooter: this.shooterId });
      if (hitMob && this.pierce > 0) {
        // Piercing: keep flying through this mob
        this.pierce--;
        this.pierced.add(hitMob.id);
        this.x += dx * (1 - entT); this.y += dy * (1 - entT); this.z += dz * (1 - entT);
        this.vy -= GRAVITY;
        this.syncView(ctx);
        return;
      }
      else if (hitPlayer) {
        const r = hitPlayer.damage(dmg, { kind: 'projectile', x: this.x - dx, y: this.y - dy, z: this.z - dz, attackerId: this.shooterId });
        if (r === 'blocked') {
          // vanilla Projectile deflection off a shield: bounce back at a fraction of the speed with a
          // little spread; the arrow is spent (no further damage) and falls / sticks normally
          const g = (): number => (ctx.host.rnd() - 0.5) * 0.04;
          this.vx = -dx * 0.2 + g(); this.vy = -dy * 0.2 + g(); this.vz = -dz * 0.2 + g();
          this.x -= dx * 0.05; this.y -= dy * 0.05; this.z -= dz * 0.05;
          this.spent = true;
          this.orient();
          this.syncView(ctx);
          return;
        }
        hitPlayer.knockback(dx, dz, 0.4 + this.punch * 0.6);
        if (this.flame && this.fromPlayer) hitPlayer.setOnFire?.(100);
      }
      this.removed = true;
      return;
    }
    if (blockT <= 1) {
      this.x += dx * blockT; this.y += dy * blockT; this.z += dz * blockT;
      this.stuck = true;
      if (this.fromPlayer) wardenHooks.vibrate(this.x, this.y, this.z, 'projectile_land'); // warden: +10 anger
      this.stuckTicks = 0;
      this.vx = this.vy = this.vz = 0;
      this.stuckCell = [Math.floor(this.x), Math.floor(this.y), Math.floor(this.z)];
      this.syncView(ctx);
      return;
    }

    this.x += dx; this.y += dy; this.z += dz;
    const wet = IS_WATERY[w.getBlock(Math.floor(this.x), Math.floor(this.y), Math.floor(this.z))] === 1;
    const drag = wet ? 0.6 : 0.99;
    this.vx *= drag; this.vy *= drag; this.vz *= drag;
    this.vy -= GRAVITY;
    this.orient();
    this.syncView(ctx);
  }

  private syncView(ctx: ArrowCtx): void {
    const v = this.view;
    v.x = this.x; v.y = this.y; v.z = this.z;
    v.px = this.px; v.py = this.py; v.pz = this.pz;
    v.stuck = this.stuck;
    v.light = ctx.light(this.x, this.y, this.z) / 15;
  }
}
